import { Toggle } from '../../components/Toggle.js';
import type { AppEndpoint, Scope } from '../../lib/types.js';

/** The picked endpoints the chosen scopes still cover, in catalog order. */
export const coveredEndpoints = (
  picked: readonly string[],
  scopes: readonly string[],
  catalog: readonly AppEndpoint[],
): string[] =>
  catalog
    .filter((e) => picked.includes(e.endpoint) && scopes.includes(e.scope))
    .map((e) => e.endpoint);

/** Step 1's switch: whether the key gets a second step to pick endpoints. */
export const LimitEndpointsToggle = ({
  limited,
  onLimitedChange,
  isDisabled,
}: {
  limited: boolean;
  onLimitedChange: (limited: boolean) => void;
  isDisabled: boolean;
}) => (
  <div className="flex items-center justify-between gap-4 rounded-md border border-outline bg-surface px-3.5 py-3">
    <div>
      <p className="text-sm font-medium text-ink">Limit this key to specific endpoints</p>
      <p className="text-xs text-ink-muted">
        For a partner that needs one lookup and nothing else.
      </p>
    </div>
    <Toggle
      checked={limited}
      onChange={() => onLimitedChange(!limited)}
      label="Limit this key to specific endpoints"
      disabled={isDisabled}
    />
  </div>
);

interface EndpointChecklistProps {
  catalog: AppEndpoint[];
  scopeCatalog: Scope[];
  selectedScopes: string[];
  /** Everything ticked, including endpoints whose scope was since unchecked. */
  picked: string[];
  onPickedChange: (picked: string[]) => void;
  isLoading: boolean;
  isDisabled: boolean;
}

/**
 * Step 2: the endpoints the selected scopes cover. One whose scope was unchecked
 * drops out, with a note, and comes back if the scope is rechecked.
 */
export const EndpointChecklist = ({
  catalog,
  scopeCatalog,
  selectedScopes,
  picked,
  onPickedChange,
  isLoading,
  isDisabled,
}: EndpointChecklistProps) => {
  const scopeName = (key: string) => scopeCatalog.find((s) => s.key === key)?.displayName ?? key;
  const dropped = catalog.filter(
    (e) => picked.includes(e.endpoint) && !selectedScopes.includes(e.scope),
  );
  const groups = selectedScopes
    .map((scope) => ({ scope, endpoints: catalog.filter((e) => e.scope === scope) }))
    .filter((g) => g.endpoints.length > 0);

  const toggle = (endpoint: string, checked: boolean) => {
    onPickedChange(checked ? [...picked, endpoint] : picked.filter((e) => e !== endpoint));
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div>
        <p className="text-sm font-medium text-ink">Pick the endpoints this key can call</p>
        <p className="text-xs text-ink-muted">
          Every other request is refused. Only endpoints this key&apos;s scopes cover are listed.
        </p>
      </div>
      {dropped.length > 0 && (
        <p className="text-xs text-ink-muted">
          Unchecking {[...new Set(dropped.map((e) => scopeName(e.scope)))].join(' and ')} took{' '}
          <span className="font-mono">{dropped.map((e) => e.endpoint).join(', ')}</span> off this
          key.
        </p>
      )}
      {isLoading ? (
        <p className="text-sm text-ink-muted">Loading endpoints…</p>
      ) : groups.length === 0 ? (
        <p className="rounded border border-dashed border-outline px-4 py-6 text-center text-sm text-ink-muted">
          None of this key&apos;s scopes has an endpoint a key can call. Go back and pick another
          scope, or turn off the limit.
        </p>
      ) : (
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
          {groups.map(({ scope, endpoints }) => {
            const count = endpoints.filter((e) => picked.includes(e.endpoint)).length;
            return (
              <section
                key={scope}
                aria-label={scopeName(scope)}
                className="overflow-hidden rounded-md border border-outline"
              >
                <div
                  className="flex items-baseline justify-between bg-surface px-3.5 py-2"
                  title={scope}
                >
                  <span className="text-[13px] font-semibold text-ink">{scopeName(scope)}</span>
                  {count > 0 && (
                    <span className="text-xs text-ink-subtle">
                      {count} of {endpoints.length}
                    </span>
                  )}
                </div>
                {endpoints.map((e) => {
                  const [method = '', path = e.endpoint] = e.endpoint.split(' ');
                  return (
                    <label
                      key={e.endpoint}
                      className="flex cursor-pointer items-center gap-3 border-t border-outline px-3.5 py-2.5 hover:bg-surface-hover"
                    >
                      <input
                        type="checkbox"
                        checked={picked.includes(e.endpoint)}
                        onChange={(ev) => toggle(e.endpoint, ev.target.checked)}
                        disabled={isDisabled}
                        className="h-4 w-4 flex-none accent-sc"
                      />
                      <span
                        className={`w-11 flex-none rounded border py-0.5 text-center font-mono text-[11px] font-bold ${
                          method === 'GET'
                            ? 'border-info-border bg-info-soft text-info'
                            : 'border-success-border bg-success-soft text-success'
                        }`}
                      >
                        {method}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-ink">
                          {(e.summary ?? path).replace(/\.$/, '')}
                        </span>
                        <span className="block break-all font-mono text-xs text-ink-subtle">
                          {path}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};
